import React, { useCallback, useEffect, useState, useRef } from "react";
import { goToPlaygroundWith } from "../../lib/playgroundLink";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Info, Layers, Copy as CopyIcon, ChevronDown, ChevronRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { okaidia } from "react-syntax-highlighter/dist/esm/styles/prism";
import "react-syntax-highlighter/dist/esm/languages/prism/javascript";
import "react-syntax-highlighter/dist/esm/languages/prism/css";
import "react-syntax-highlighter/dist/esm/languages/prism/markup"; 
import "react-syntax-highlighter/dist/esm/languages/prism/cpp"; 
import "react-syntax-highlighter/dist/esm/languages/prism/python";
import "react-syntax-highlighter/dist/esm/languages/prism/php";
import "react-syntax-highlighter/dist/esm/languages/prism/java";
import "react-syntax-highlighter/dist/esm/languages/prism/csharp";
import "react-syntax-highlighter/dist/esm/languages/prism/go";
import "react-syntax-highlighter/dist/esm/languages/prism/rust";
import "react-syntax-highlighter/dist/esm/languages/prism/dart";


const API_URL = process.env.REACT_APP_API_URL; 

const getSortedItems = (items = []) =>
  [...items].sort((a, b) => (a.order || 0) - (b.order || 0));

const LessonDetailPage = () => {
  const { slug, lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completingLesson, setCompletingLesson] = useState(false);
  const [subTopicsExpanded, setSubTopicsExpanded] = useState(true);
  const [expandedSidebarLessons, setExpandedSidebarLessons] = useState({});
  const [expandedTopicPaths, setExpandedTopicPaths] = useState({});
  const [activeTopicPath, setActiveTopicPath] = useState([]);

  // Get user info for access control
  const user = JSON.parse(localStorage.getItem("user"));
  const isAdminOrMentor = user && (user.role === "admin" || user.role === "mentor");
  const isPremiumUser = user && user.role === "premium";

  // Quiz state
  const [quizStep, setQuizStep] = useState(0);
  const [quizScore, setQuizScore] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  // Scroll effect ref
  const mainRef = useRef(null);

  // Fetch course and lesson
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const courseRes = await axios.get(`${API_URL}/courses/slug/${slug}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!courseRes.data.success) throw new Error("Failed to fetch course");
        setCourse(courseRes.data.data);

        // Find lesson in modules
       let foundLesson = null;
         for (const mod of courseRes.data.data.modules) {
            foundLesson =
              (mod.lessons || []).find((l) => l._id === lessonId || l.id === lessonId) ||
              (mod.lessons || [])
                .flatMap((l) => l.subLessons || [])
                .find((l) => l._id === lessonId || l.id === lessonId);
        if (foundLesson) break;
      }
        setLesson(foundLesson);
        setSubTopicsExpanded(true);
        setExpandedTopicPaths({});
        setActiveTopicPath([]);
        if (lessonId) {
          setExpandedSidebarLessons((prev) => ({ ...prev, [lessonId]: true }));
        }
        setQuizStep(0);
        setQuizScore(0);
        setQuizDone(false);
        setSelectedOption(null);
        setShowFeedback(false);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load lesson. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [slug, lessonId, location.state?.subTopicSlug]);

  // Improved scroll effect for sidebar collapse (desktop only)
  useEffect(() => {
    const handleScroll = () => {
      // Only apply collapse effect on desktop
      if (window.innerWidth < 768) return;
      
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const clientHeight = document.documentElement.clientHeight;
      
      // Calculate if we're near the bottom (within 50px)
      const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;
      
      // Update state to trigger re-render
      setSidebarCollapsed(isNearBottom);
    };

    // Add scroll listener
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    
    // Check initial state
    handleScroll();
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  // Reset quiz when switching lessons or sub-topics
  useEffect(() => {
    setQuizStep(0);
    setQuizScore(0);
    setQuizDone(false);
    setSelectedOption(null);
    setShowFeedback(false);
  }, [lessonId]);

  // Interactive quiz logic
  const handleQuizAnswer = (quizSource, optionIdx) => {
    if (showFeedback || !quizSource?.quiz?.length) return;
    setSelectedOption(optionIdx);
    setShowFeedback(true);
    const isCorrect = optionIdx === quizSource.quiz[quizStep].correctAnswer;
    if (isCorrect) setQuizScore((s) => s + 1);

    setTimeout(() => {
      setShowFeedback(false);
      setSelectedOption(null);
      if (quizStep + 1 < quizSource.quiz.length) {
        setQuizStep((s) => s + 1);
      } else {
        setQuizDone(true);
      }
    }, 1000);
  };

  // Handle lesson completion
  const markLessonComplete = async () => {
    if (!course?._id || !lesson?._id) return;
    
    setCompletingLesson(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/progress/${course._id}/lessons/${lesson._id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          completed: true,
          timeSpent: 0, // You can track actual time spent
          quizScore: quizDone ? quizScore : null,
          challengeCompleted: false
        })
      });

      if (response.ok) {
        setIsCompleted(true);
        // You could also show a success message or redirect to next lesson
        console.log('Lesson marked as complete!');
      } else {
        console.error('Failed to mark lesson as complete');
      }
    } catch (error) {
      console.error('Error marking lesson as complete:', error);
    } finally {
      setCompletingLesson(false);
    }
  };

  // Check if lesson is already completed
  useEffect(() => {
    const checkLessonCompletion = async () => {
      if (!course?._id || !lesson?._id) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/progress/${course._id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.lessons) {
            const completedLesson = data.data.lessons.find(l => l._id === lesson._id);
            setIsCompleted(!!completedLesson?.isCompleted);
          }
        }
      } catch (error) {
        console.error('Error checking lesson completion:', error);
      }
    };

    checkLessonCompletion();
  }, [course?._id, lesson?._id]);

  const lessonChildren = getSortedItems(lesson?.subLessons || lesson?.children || []);
  const hasNestedLessons = lessonChildren.length > 0;
  const hasSubTopics = !hasNestedLessons && Array.isArray(lesson?.subTopics) && lesson.subTopics.length > 0;

  const getTopicNodeAtPath = (nodes = [], path = []) => {
    if (!Array.isArray(nodes) || !path.length) return null;
    let current = nodes[path[0]];
    if (!current) return null;
    for (let index = 1; index < path.length; index += 1) {
      const nextNodes = Array.isArray(current.topics) ? current.topics : Array.isArray(current.children) ? current.children : [];
      current = nextNodes[path[index]];
      if (!current) return null;
    }
    return current;
  };

  const findFirstLeafPath = useCallback((nodes = [], path = []) => {
    for (let index = 0; index < nodes.length; index += 1) {
      const currentPath = [...path, index];
      const node = nodes[index];
      const childNodes = Array.isArray(node?.topics) ? node.topics : Array.isArray(node?.children) ? node.children : [];
      if (childNodes.length > 0) {
        const nestedPath = findFirstLeafPath(childNodes, currentPath);
        if (nestedPath) return nestedPath;
      } else {
        return currentPath;
      }
    }
    return null;
  }, []);

  useEffect(() => {
    if (!hasSubTopics) return;

    const initialPath = findFirstLeafPath(lesson?.subTopics || []);
    if (!initialPath) return;

    const expandedAncestors = {};
    for (let index = 1; index < initialPath.length; index += 1) {
      expandedAncestors[initialPath.slice(0, index).join('.')] = true;
    }

    setExpandedTopicPaths((prev) => ({ ...prev, ...expandedAncestors }));
    setActiveTopicPath(initialPath);
  }, [findFirstLeafPath, hasSubTopics, lesson?._id, lesson?.subTopics]);

  const renderQuizInteractive = (quizSource) => {
    if (!quizSource?.quiz || quizSource.quiz.length === 0) return null;
    if (quizDone) {
      return (
        <div className="quiz-block my-4 p-4 rounded bg-yellow-50 dark:bg-yellow-50  text-black  border-l-4 border-yellow-400 dark:border-yellow-500">
          <h2 className="font-semibold mb-2 text-yellow-700 dark:text-yellow-700">Quiz Complete!</h2>
          <p className="text-lg">
            Your score: <span className="font-bold">{quizScore}</span> / {quizSource.quiz.length}
          </p>
        </div>
      );
    }
    const q = quizSource.quiz[quizStep];
    return (
      <div className="quiz-block my-4 p-4 rounded bg-yellow-50 dark:bg-yellow-50 text-black border-l-4 border-yellow-400 dark:border-yellow-500">
        <h2 className="font-semibold mb-2 text-yellow-700 dark:text-yellow-700">Quiz</h2>
        <div className="font-medium mb-2">{q.question}</div>
        <ul className="list-disc ml-6">
          {q.options.map((opt, i) => {
            let optionClass = "cursor-pointer p-2 rounded transition-colors";
            if (showFeedback) {
              if (i === q.correctAnswer) optionClass += " bg-green-700 dark:bg-green-700";
              else if (i === selectedOption) optionClass += " bg-red-700 dark:bg-red-700";
            } else if (selectedOption === i) {
              optionClass += "bg-primary/20 dark:bg-primary/20";
            }
            return (
              <li
                key={i}
                className={optionClass}
                onClick={() => !showFeedback && handleQuizAnswer(quizSource, i)}
                style={{ pointerEvents: showFeedback ? "none" : "auto" }}
              >
                {opt}
              </li>
            );
          })}
        </ul>
        <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Question {quizStep + 1} of {quizSource.quiz.length}
        </div>
      </div>
    );
  };

  const renderExercises = (source) =>
    source?.exercises && source.exercises.length > 0 ? (
      <div className="exercise-block my-4 p-4 rounded bg-gray-100 dark:bg-gray-800 border-l-4 border-primary">
        <h2 className="font-semibold mb-2 text-primary flex items-center">
          <BookOpen className="mr-2" /> Practice Exercises
        </h2>
        <ol className="list-decimal ml-6 space-y-1">
          {source.exercises.map((ex, idx) => (
            <li key={idx}>{ex}</li>
          ))}
        </ol>
      </div>
    ) : null;

  const renderConcepts = (concepts) =>
    concepts && concepts.trim().length > 0 ? (
      <div className="my-4 rounded border border-green-200 bg-primary p-4 dark:border-green-100 dark:bg-green-500">
        <h3 className="mb-2 text-base font-semibold text-white-100 dark:text-purple-100">Key Concepts</h3>
        <ul className="list-disc ml-6 space-y-1 text-sm text-gray-800 dark:text-gray-200">
          {concepts
            .split("\n")
            .map((line) => line.replace(/^\s*[-*]\s*/, "").trim())
            .filter(Boolean)
            .map((line, idx) => (
              <li key={idx}>{line}</li>
            ))}
        </ul>
      </div>
    ) : null;

  const renderIllustration = (source) => {
    const src = source?.imageUrl || source?.illustration?.src;
    if (!src) return null;

    return (
      <div className="mb-4 overflow-hidden rounded-md border border-emerald-100 bg-white dark:border-emerald-800 dark:bg-slate-950">
        <div className="mx-auto flex max-w-[420px] items-center justify-center bg-white p-2 dark:bg-slate-950">
          <img
            src={src}
            alt={source?.illustration?.alt || `${source?.title || 'Lesson'} illustration`}
            className="block h-auto max-h-[240px] w-full object-contain"
            loading="lazy"
            style={{ display: 'block', margin: '0 auto', maxWidth: '360px' }}
          />
        </div>
      </div>
    );
  };

    
  
  // Sidebar: modules and lessons
  const Sidebar = () =>
    course ? (
      <>
        {/* Mobile Menu Button */}
        <button
          className="md:hidden fixed top-4 left-4 z-50 bg-primary text-black p-2 rounded-lg shadow-lg"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>

        {/* Mobile Backdrop */}
        {mobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside 
          className={`
            fixed top-0 left-0 h-screen bg-gray-50 dark:bg-slate-800 
            border-r border-gray-200 dark:border-slate-700 p-4 overflow-y-auto z-40
            transition-all duration-300 ease-in-out
            
            /* Mobile styles */
            md:z-10
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            
            /* Desktop styles */
            ${sidebarCollapsed ? 'md:w-12' : 'w-56 md:w-56'}
          `}
        >
          {(!sidebarCollapsed || window.innerWidth < 768) && (
            <>
              <h2 className="text-lg font-bold mb-4">Course Content</h2>
              {course.modules.map((mod, mi) => (
                <div key={mi} className="mb-4">
                  <div className="font-semibold mb-2">{mod.title}</div>
                  <ul>
                    {(mod.lessons || []).map((les) => {
                      const lesId = les._id || les.id;
                      const isActiveLesson = lesId === lessonId;
                      const lessonChildren = getSortedItems(les.subLessons || les.children || []);
                      const hasLessonChildren = lessonChildren.length > 0;
                      const isSidebarExpanded = !!expandedSidebarLessons[lesId];

                      return (
                        <li key={lesId} className="mb-1">
                          <div className="flex items-start gap-1">
                            {hasLessonChildren ? (
                              <button
                                type="button"
                                className="mt-1 rounded p-0.5 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"
                                onClick={() =>
                                  setExpandedSidebarLessons((prev) => ({
                                    ...prev,
                                    [lesId]: !isSidebarExpanded,
                                  }))
                                }
                                aria-label={isSidebarExpanded ? "Collapse topics" : "Expand topics"}
                              >
                                {isSidebarExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </button>
                            ) : (
                              <span className="w-5" />
                            )}
                            <button
                              className={`flex-1 px-2 py-1 rounded text-left transition
                                ${isActiveLesson
                                  ? "bg-primary dark:bg-primary font-bold"
                                  : "hover:bg-primary/10 dark:hover:bg-gray-800"
                                }`}
                              onClick={() => {
                                navigate(`/courses/${slug}/lessons/${lesId}`);
                                if (hasLessonChildren) {
                                  setExpandedSidebarLessons((prev) => ({ ...prev, [lesId]: true }));
                                }
                                setMobileMenuOpen(false);
                              }}
                            >
                              {les.title}
                            </button>
                          </div>
                          {hasLessonChildren && isSidebarExpanded && (
                            <ul className="ml-6 mt-1 space-y-1 border-l border-gray-200 pl-2 dark:border-gray-700">
                              {lessonChildren.map((childLesson) => {
                                const childId = childLesson._id || childLesson.id;
                                const isActiveChild = isActiveLesson && lessonId === childId;

                                return (
                                  <li key={childId}>
                                    <button
                                      type="button"
                                      className={`w-full rounded px-2 py-1 text-left text-sm transition ${
                                        isActiveChild
                                          ? "bg-emerald-600 font-semibold text-white"
                                          : "text-gray-600 hover:bg-emerald-50 dark:text-gray-300 dark:hover:bg-slate-800"
                                      }`}
                                      onClick={() => {
                                        navigate(`/courses/${slug}/lessons/${childId}`);
                                        setExpandedSidebarLessons((prev) => ({ ...prev, [lesId]: true }));
                                        setMobileMenuOpen(false);
                                      }}
                                    >
                                      {childLesson.title}
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </>
          )}
          {sidebarCollapsed && window.innerWidth >= 768 && (
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-primary rounded mb-2"></div>
              <div className="w-6 h-1 bg-gray-300 rounded mb-1"></div>
              <div className="w-6 h-1 bg-gray-300 rounded mb-1"></div>
              <div className="w-6 h-1 bg-gray-300 rounded"></div>
            </div>
          )}
        </aside>
      </>
    ) : null;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-red-800 font-semibold mb-2">Error Loading Lesson</h2>
          <p className="text-red-600">{error}</p>
          <Link to={`/courses/${slug}`} className="text-primary mt-4 inline-block">
            ← Back to Course
          </Link>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h2 className="text-yellow-800 font-semibold mb-2">Lesson Not Found</h2>
          <p className="text-yellow-600">The lesson you're looking for doesn't exist or has been removed.</p>
          <Link to={`/courses/${slug}`} className="text-primary mt-4 inline-block">
            ← Back to Course
          </Link>
        </div>
      </div>
    );
  }

  // Check premium access for premium courses
  if (course?.isPremium && !isAdminOrMentor && !isPremiumUser && !lesson?.isFree) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-blue-800 font-semibold mb-2">🔒 Premium Content</h2>
          <p className="text-blue-600 mb-4">This lesson is part of a premium course. Subscribe to get access to all premium content.</p>
          <div className="space-y-3">
            <p className="text-sm text-blue-700">With premium access, you get:</p>
            <ul className="list-disc ml-6 text-sm text-blue-700 space-y-1">
              <li>Access to all premium courses and lessons</li>
              <li>1-on-1 mentorship sessions</li>
              <li>Code reviews by experts</li>
              <li>Certification upon completion</li>
            </ul>
          </div>
          <div className="mt-6 flex gap-3">
            <Link 
              to="/pricing" 
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Get Premium Access
            </Link>
            <Link 
              to={`/courses/${slug}`} 
              className="text-blue-600 px-4 py-2 border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
            >
              ← Back to Course
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Multi-section parser: handles multiple repeating Code Examples + Explanation pairs
  const parseLessonSections = (raw) => {
    if (!raw) return null;
    const pairRegex = /###\s+Code Examples?[\r\n]+([\s\S]*?)###\s+Explanation[s]?[\r\n]+([\s\S]*?)(?=(?:\n###\s+Code Examples?|\n###\s+Practice|\n##\s+Additional|$))/gi;
  let match; const sections = []; let firstStart = null; let lastEnd = 0;
    while ((match = pairRegex.exec(raw)) !== null) {
      const [full, codePart, explanationPart] = match;
      if (firstStart === null) firstStart = match.index;
      lastEnd = match.index + full.length;
      // Extract first code fence (or treat entire codePart)
      let fenceMatch = codePart.match(/```(\w+)?\r?\n([\s\S]*?)```/);
      let codeLang = fenceMatch ? (fenceMatch[1] || 'text').trim() : 'text';
      let codeBody = fenceMatch ? fenceMatch[2] : codePart.trim();
      if (!fenceMatch && codeBody.trim().startsWith('```')) {
        codeBody = codeBody.trim().replace(/^```(\w+)?\r?\n/, '').replace(/\r?\n```$/, '');
      }
      sections.push({ codeLang, codeBody, explanationMd: explanationPart.trim() });
  // advance regex automatically; no need to store lastIndex
    }
    if (!sections.length) return null;
    const beforeAll = raw.slice(0, firstStart);
    const afterAll = raw.slice(lastEnd);
    return { beforeAll, sections, afterAll };
  };
  const multi = parseLessonSections(lesson.content);

  // Map short language names to what prism expects
  // Map possible shorthand or enum language values to Prism identifiers
  const languageMapFull = {
    js: 'javascript', javascript: 'javascript',
    html: 'markup', xml: 'markup',
    c: 'cpp', cpp: 'cpp', cplusplus: 'cpp',
    css: 'css',
    php: 'php',
    py: 'python', python: 'python',
    dart: 'dart',
    java: 'java',
    csharp: 'csharp', cs: 'csharp',
    go: 'go', golang: 'go',
    rust: 'rust',
    plaintext: 'text', text: 'text'
  };

  // Split a combined codeBody into multiple example objects based on comment markers
  const splitExamples = (codeBody) => {
    if (!codeBody) return [];
    const lines = codeBody.split('\n');
    const examples = [];
    let current = null;
    lines.forEach(line => {
      const exMatch = /^\s*(?:\/\/|#)\s*Example\s+(\d+)(?::|-)?\s*(.*)$/i.exec(line);
      if (exMatch) {
        if (current) examples.push(current);
        const num = exMatch[1];
        const titleRest = exMatch[2].trim();
        current = { number: parseInt(num,10), title: `Example ${num}${titleRest?': '+titleRest:''}`, code: [] };
      } else if (current) {
        current.code.push(line);
      } else {
        // If no explicit Example marker yet, start implicit Example 1
        if (line.trim().length) {
          current = { number: 1, title: 'Example 1', code: [line] };
        }
      }
    });
    if (current) examples.push(current);
    // Fallback single block if regex didn't find markers
    if (examples.length === 1 && examples[0].title === 'Example 1' && !/^\s*(?:\/\/|#)\s*Example/i.test(codeBody)) {
      examples[0].title = 'Code Example';
    }
    return examples.map(e => ({ ...e, code: e.code.join('\n').trim() }));
  };

  const splitExplanations = (explanationMd) => {
    if (!explanationMd) return { general: '' };
    const map = {};
    const regex = /(?:^|\n)\s*(?:###|####)?\s*Example\s+(\d+).*?\n([\s\S]*?)(?=(?:\n(?:###|####)?\s*Example\s+\d+)|$)/gi;
    let m; let foundSpecific = false;
    while ((m = regex.exec(explanationMd))) {
      foundSpecific = true;
      map[parseInt(m[1],10)] = m[2].trim();
    }
    if (!foundSpecific) map.general = explanationMd.trim();
    return map;
  };

  const buildSectionExamples = (section) => {
    const examples = splitExamples(section.codeBody);
    const explanationsMap = splitExplanations(section.explanationMd);
    return { examples, explanationsMap };
  };
  const sectionData = multi ? multi.sections.map(buildSectionExamples) : [];

  // Simple sanitizer that strips script tags (sandbox also blocks scripts)
  const sanitizeHtml = (html) => html
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/on\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/on\w+\s*=\s*'[^']*'/gi, '')
    .replace(/on\w+\s*=\s*[^\s>]+/gi, '');

  // Build iframe srcDoc for html/css examples
  const buildPreviewDoc = (lang, code) => {
    const baseStyles = `
      *{box-sizing:border-box} body{margin:0;padding:16px;font-family:system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,Cantarell,Noto Sans,sans-serif;background:#f9fafb;color:#111827}
      .grid{display:grid;gap:12px}
      .row{display:flex;gap:12px;flex-wrap:wrap}
      .box{width:100px;height:100px;background:#10b981;color:#fff;display:flex;align-items:center;justify-content:center;border-radius:8px}
      img.preview{max-width:140px;border-radius:8px}
      .card{padding:12px;border:1px solid #e5e7eb;border-radius:8px;background:#fff}
      .title{font-weight:600;margin:0 0 8px}
      .note{font-size:12px;color:#6b7280;margin-top:8px}
    `;

    // Detect and split combined blocks like /*HTML*/ ... /*CSS*/ ...
    const htmlMarker = /\/\*\s*HTML\s*\*\//i;
    const cssMarker = /\/\*\s*CSS\s*\*\//i;
    if (htmlMarker.test(code) && cssMarker.test(code)) {
      let htmlPart = '';
      let cssPart = '';
      // Find positions to decide ordering
      const htmlIdx = code.search(htmlMarker);
      const cssIdx = code.search(cssMarker);
      if (htmlIdx < cssIdx) {
        const between = code.slice(htmlIdx).split(cssMarker);
        htmlPart = between[0].replace(htmlMarker, '').trim();
        cssPart = between.slice(1).join(cssMarker.source).trim();
      } else {
        const between = code.slice(cssIdx).split(htmlMarker);
        cssPart = between[0].replace(cssMarker, '').trim();
        htmlPart = between.slice(1).join(htmlMarker.source).trim();
      }
      const safeHtml = sanitizeHtml(htmlPart);
      return `<!doctype html><html><head><meta charset="utf-8"/><style>${baseStyles}\n${cssPart}</style></head><body>${safeHtml}</body></html>`;
    }

  if (lang === 'markup' || /<\w+[^>]*>/.test(code)) {
      const safe = sanitizeHtml(code.trim());
      const hasHtmlShell = /<html[\s\S]*<\/html>/i.test(safe);
      if (hasHtmlShell) return safe; // already a full doc
      return `<!doctype html><html><head><meta charset="utf-8"/><style>${baseStyles}</style></head><body>${safe}</body></html>`;
    }
    if (lang === 'css') {
      const css = code.trim();
      // Richer demo markup to visualize a variety of CSS selectors and common UI patterns
      const demo = `
        <div class="preview-surface">
          <header class="site-header">
            <div class="brand">Course Preview</div>
            <nav class="nav">
              <a href="#">Home</a>
              <a href="#">Docs</a>
              <a href="#">Contact</a>
            </nav>
          </header>

          <main class="content">
            <section class="hero">
              <h2>Hero Title</h2>
              <p>This area demonstrates typography, spacing and hero styles.</p>
              <button class="btn-primary">Primary Action</button>
            </section>

            <section class="cards-row">
              <article class="card">
                <h3>Card One</h3>
                <p>Supports layout, box-shadow, and border-radius styles.</p>
              </article>
              <article class="card">
                <h3 id="special">Card Two</h3>
                <p>Target by id and descendant selectors.</p>
              </article>
              <article class="card circle">
                <h3>Avatar</h3>
              </article>
            </section>

            <section class="form-sample">
              <label>Subscribe</label>
              <input placeholder="Enter email" />
              <button class="btn-ghost">Subscribe</button>
            </section>
          </main>

          <footer class="site-footer">CSS Preview Surface — adjust styles to see live changes</footer>
        </div>`;

      return `<!doctype html><html><head><meta charset="utf-8"/><style>${baseStyles}\n${css}</style></head><body>${demo}</body></html>`;
    }
    return null;
  };

  const CodeExampleBlock = ({ language, example }) => {
    const lang = languageMapFull[language.toLowerCase()] || language.toLowerCase();
    const [copied, setCopied] = useState(false);
    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(example.code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      } catch (_) {
        // no-op
      }
    };
  const canPreview = lang === 'markup' || lang === 'css' || /<\w+[^>]*>/.test(example.code || '') || /\/\*\s*(HTML|CSS)\s*\*\//i.test(example.code || '');
    const srcDoc = canPreview ? buildPreviewDoc(lang, example.code) : null;
    return (
      <div className="code-example-block my-6 mx-0 w-full min-w-0 max-w-none overflow-hidden rounded-lg border border-green-700 bg-neutral-900 shadow-sm md:-mx-4 md:w-[calc(100%+2rem)]">
        <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-2 bg-green-800 px-4 py-2 text-sm font-medium text-green-100">
          <span className="min-w-0 break-words font-semibold">{example.title}</span>
          <div className="flex shrink-0 flex-wrap gap-2">
            {canPreview && (
              <button
                onClick={() => {
                  try {
                    // Pass the prepared srcDoc to the preview page
                    sessionStorage.setItem('preview:incomingSrcDoc', srcDoc || '');
                    sessionStorage.setItem('preview:incomingTitle', `${lesson?.title || ''} - ${example.title || ''}`);
                  } catch (e) {}
                  navigate(`/courses/${encodeURIComponent(slug)}/preview`);
                }}
                className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-xs"
              >
                Preview
              </button>
            )}
            <button
              onClick={handleCopy}
              className="px-2 py-1 rounded bg-green-900 hover:bg-green-700 text-xs flex items-center justify-center"
              aria-label={copied ? "Copied" : "Copy code"}
              title={copied ? "Copied" : "Copy code"}
            >
              <CopyIcon className="w-4 h-4" />
            </button>
            {copied && (
              <span
                className="ml-1 px-1.5 py-0.5 rounded bg-emerald-700 text-white text-[10px]"
                aria-live="polite"
              >
                Copied
              </span>
            )}
            <button
              onClick={() => {
                try {
                  if (lang === 'markup' || /<\w+[^>]*>/.test(example.code || '')) {
                    goToPlaygroundWith({ html: example.code || '' });
                  } else if (lang === 'css') {
                    goToPlaygroundWith({ css: example.code || '' });
                  } else {
                    // Use mapped language where possible
                    const mapped = (language && language.toLowerCase()) || 'text';
                    goToPlaygroundWith({ code: example.code || '', lang: mapped });
                  }
                } catch (e) { /* ignore */ }
              }}
              className="px-2 py-1 rounded bg-green-600 hover:bg-green-500 text-xs"
            >
              Play
            </button>
          </div>
        </div>
        {srcDoc && (
          <div className="bg-white">
            <iframe
              title={`preview-${example.number}`}
              sandbox="allow-same-origin"
              style={{ width: '100%', height: 320, border: 'none' }}
              srcDoc={srcDoc}
            />
          </div>
        )}
        <SyntaxHighlighter
          style={okaidia}
          language={lang}
          PreTag="div"
          showLineNumbers
          className="!m-0 w-full max-w-full overflow-x-auto"
          customStyle={{
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            padding: '1.25rem',
            fontSize: 14,
            overflowX: 'auto',
            whiteSpace: 'pre',
            overflowWrap: 'normal',
            wordBreak: 'normal'
          }}
        >
          {example.code}
        </SyntaxHighlighter>
      </div>
    );
  };
  const ExplanationBlock = ({ markdown }) => (
    <div className="explanation-block -mt-2 mb-8 mx-0 w-full min-w-0 max-w-none overflow-x-auto overflow-y-hidden rounded-md border-l-4 border-green-500 bg-green-50 p-4 text-sm leading-relaxed dark:bg-emerald-900/40 md:-mx-4 md:w-[calc(100%+2rem)]">
      <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
    </div>
  );

  const renderNestedLessons = () => {
    if (!hasNestedLessons) return null;

    return (
      <div className="space-y-4">
        <div className="rounded-md border border-emerald-200 bg-white p-4 dark:border-emerald-800 dark:bg-slate-800">
          <h3 className="mb-2 text-lg font-semibold text-green-600 dark:text-green-200">Lesson Topics</h3>
          {lesson?.content && (
            <div className="markdown-content mb-4">
              <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                {lesson.content}
              </ReactMarkdown>
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            {lessonChildren.map((childLesson) => {
              const childId = childLesson._id || childLesson.id;
              return (
                <Link
                  key={childId}
                  to={`/courses/${slug}/lessons/${childId}`}
                  className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm font-medium text-primary hover:bg-emerald-50 dark:border-gray-700 dark:bg-slate-800 dark:hover:bg-slate-700"
                >
                  <div className="font-semibold">{childLesson.title}</div>
                  {childLesson.description && (
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{childLesson.description}</p>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderSubTopics = () => {
    if (!hasSubTopics) return null;

    const groupTitle = lesson.topic || lesson.title;
    const activeTopic = getTopicNodeAtPath(lesson.subTopics || [], activeTopicPath);

    const renderTopicTree = (nodes = [], path = []) => (
      <div className="space-y-1">
        {nodes.map((node, index) => {
          const nodePath = [...path, index];
          const nodeKey = nodePath.join('.');
          const childNodes = Array.isArray(node?.topics) ? node.topics : Array.isArray(node?.children) ? node.children : [];
          const hasChildren = childNodes.length > 0;
          const isExpanded = expandedTopicPaths[nodeKey];
          const isActive = activeTopicPath.join('.') === nodeKey;

          return (
            <div key={nodeKey}>
              <button
                type="button"
                onClick={() => {
                  if (hasChildren) {
                    setExpandedTopicPaths((prev) => ({ ...prev, [nodeKey]: !isExpanded }));
                  }
                  setActiveTopicPath(nodePath);
                }}
                className={`flex w-full items-start gap-2 rounded px-3 py-2 text-left text-sm transition ${
                  isActive
                    ? 'bg-emerald-500 text-white shadow-sm ring-2 ring-emerald-300 dark:ring-emerald-400'
                    : 'text-gray-700 hover:bg-emerald-50 dark:text-gray-200 dark:hover:bg-slate-800'
                }`}
              >
                <span className="mt-0.5 text-xs">
                  {hasChildren ? (isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="inline-block h-4 w-4" />}
                </span>
                <span className="font-medium">{node.title}</span>
              </button>
              {hasChildren && isExpanded && (
                <div className="ml-4 mt-1 border-l border-gray-200 pl-3 dark:border-gray-700">
                  {renderTopicTree(childNodes, nodePath)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );

    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setSubTopicsExpanded((open) => !open)}
          className="flex w-full items-center justify-between rounded-md border border-emerald-200 bg-white px-4 py-3 text-left font-semibold text-emerald-900 transition hover:bg-emerald-50 dark:border-emerald-800 dark:bg-slate-900 dark:text-emerald-100 dark:hover:bg-slate-800"
          aria-expanded={subTopicsExpanded}
        >
          <span className="flex items-center gap-2">
            {subTopicsExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {groupTitle}
          </span>
          <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
            {lesson.subTopics?.length || 0} topics
          </span>
        </button>

        {subTopicsExpanded && (
          <div className="grid min-w-0 gap-3 lg:grid-cols-[190px_minmax(0,1fr)] xl:grid-cols-[210px_minmax(0,1fr)]">
            <div className="min-w-0 rounded-md border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-slate-900">
              {renderTopicTree(lesson.subTopics || [])}
            </div>

            <div className="min-w-0 max-w-full rounded-md border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
              <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">{activeTopic?.title || lesson.title}</h3>
              {renderIllustration(activeTopic)}
              {activeTopic?.description && (
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">{activeTopic.description}</p>
              )}
              {renderConcepts(activeTopic?.concepts)}
              {activeTopic?.content && (
                <div className="markdown-content mb-4">
                  <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                    {activeTopic.content}
                  </ReactMarkdown>
                </div>
              )}
              {(activeTopic?.codeExamples || []).map((ex, i) => (
                <div key={`${activeTopic.slug || activeTopic.title}-${i}`} className="mb-8 last:mb-0">
                  <CodeExampleBlock language={ex.language || 'javascript'} example={{ number: i + 1, title: ex.title, code: ex.code }} />
                  <ExplanationBlock markdown={ex.explanation} />
                </div>
              ))}
              {renderExercises(activeTopic)}
              {renderQuizInteractive(activeTopic)}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Custom Markdown renderers for headings, lists, and code blocks
  const markdownComponents = {
  h2: ({ node, children, ...props }) => <h2 className="text-lg font-bold mt-6 mb-2 text-primary dark:text-primary" {...props}>{children}</h2>,
  h3: ({ node, children, ...props }) => <h3 className="text-base font-semibold mt-4 mb-2 text-primary dark:text-primary" {...props}>{children}</h3>,
    ul: ({ node, ...props }) => <ul className="list-disc ml-6 mb-4" {...props} />,
    ol: ({ node, ...props }) => <ol className="list-decimal ml-6 mb-4" {...props} />,
    li: ({ node, ...props }) => <li className="mb-1" {...props} />,
    table: ({ node, ...props }) => (
      <div className="my-3 max-w-full overflow-x-auto overflow-y-hidden rounded-md border border-gray-200 dark:border-gray-700">
        <table className="w-max min-w-full table-auto text-sm divide-y divide-gray-200 dark:divide-gray-700" {...props} />
      </div>
    ),
    thead: ({ node, ...props }) => <thead className="bg-gray-100 dark:bg-gray-800" {...props} />,
    tbody: ({ node, ...props }) => <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800" {...props} />,
    tr: ({ node, ...props }) => <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors" {...props} />,
    th: ({ node, ...props }) => <th scope="col" className="whitespace-nowrap px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200" {...props} />,
    td: ({ node, ...props }) => <td className="whitespace-nowrap px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 align-top [&>p]:m-0 [&>p]:inline" {...props} />,
    

    
  code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || "");
      const languageMap = {
        js: 'javascript', javascript: 'javascript',
        html: 'markup', xml: 'markup',
        c: 'cpp', cpp: 'cpp', cplusplus: 'cpp',
        css: 'css',
        php: 'php',
        py: 'python', python: 'python',
        dart: 'dart',
        java: 'java',
        csharp: 'csharp', cs: 'csharp',
        go: 'go', golang: 'go',
        rust: 'rust',
        plaintext: 'text', text: 'text'
      };

  const language = match ? languageMap[match[1].toLowerCase()] || match[1].toLowerCase() : null;

      return !inline && match ? (
        <div className="relative">
          <SyntaxHighlighter
            style={okaidia}
            language={language}
            PreTag="div"
            className="my-4 max-w-none overflow-x-auto rounded-lg"
              showLineNumbers={true}
        wrapLines={true}
        wrapLongLines={false}
        customStyle={{
          padding: "1.5em",
          fontSize: "14px",
          lineHeight: "1.5",
          maxWidth: "none",
          overflowX: "auto",
          whiteSpace: "pre",
          overflowWrap: "normal",
          wordBreak: "normal"
        }}
          >
            {String(children).replace(/\n$/, "")}
          </SyntaxHighlighter>
          <button
            className="absolute bottom-4 right-4 bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded shadow text-xs"
            onClick={() => {
              try {
                const code = String(children).replace(/\n$/, "");
                goToPlaygroundWith({ code, lang: language });
              } catch (e) {}
            }}
          >
            Play
          </button>
        </div>
      ) : (
        <code className="bg-slate-100 dark:bg-slate-300 px-1 rounded text-red-500">{children}</code>
      );
    }
  };

  return (
    <div className="flex min-h-screen min-w-0 max-w-full overflow-x-hidden">
      <Sidebar />
      <main
        ref={mainRef}
        className={`
          min-w-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto px-0 py-4 md:p-6 light:bg-white dark:bg-slate-800
          transition-all duration-300 ease-in-out
          
          /* Mobile: no left margin, full width */
          ml-0
          
          /* Desktop: adjust margin based on sidebar state */
          ${sidebarCollapsed ? 'md:ml-12' : 'md:ml-56'}
        `}
      >
        <Link to={`/courses/${slug}`} className="text-primary mb-4 inline-block">
          ← Back to Course
        </Link>
        <div className="min-h-screen min-w-0 max-w-full overflow-x-hidden rounded-lg bg-neutral-50 px-0 py-4 shadow-md dark:bg-slate-800 md:px-6 md:py-6">
          <h1 className="text-2xl md:text-3xl font-bold mb-4">{lesson.title}</h1>
          {/* Overview Section */}
          <div className="mb-6">
            <div className="flex items-center mb-2">
              <Info className="mr-2 text-primary" />
              <h2 className="text-xl font-bold">Overview</h2>
            </div>
            <div className="bg-primary dark:bg-primary rounded p-4 mb-4">
              <ReactMarkdown components={markdownComponents}remarkPlugins={[remarkGfm]}>
                {lesson.overview || lesson.description}
              </ReactMarkdown>
            </div>
            {/* Topic Section */}
            {lesson.topic && (
              <>
                <div className="flex items-center mb-2">
                  <Layers className="mr-2 text-green-500" />
                  <h2 className="text-xl font-bold">Topic</h2>
                </div>
                <div className="bg-primary dark:bg-green-500 rounded p-4 mb-4">
                  <span className="font-semibold">{lesson.topic}</span>
                </div>
              </>
            )}
            {/* Practice Exercises */}
            {!hasNestedLessons && !hasSubTopics && renderExercises(lesson)}
            {/* Lesson Content Section */}
            <div className="flex items-center mb-2">
              <BookOpen className="mr-2 text-primary" />
              <h2 className="text-xl font-bold">Lesson Content</h2>
            </div>
            <div className="markdown-content min-w-0 max-w-full overflow-x-hidden rounded bg-green-100 p-4 mb-4 dark:bg-slate-800">
              {hasNestedLessons ? (
                renderNestedLessons()
              ) : hasSubTopics ? (
                renderSubTopics()
              ) : lesson.codeExamples && lesson.codeExamples.length ? (
                <>
                  {lesson.codeExamples.map((ex, i) => (
                    <div key={i} className="mb-8">
                      <CodeExampleBlock language={ex.language || 'javascript'} example={{ number: i+1, title: ex.title, code: ex.code }} />
                      <ExplanationBlock markdown={ex.explanation} />
                    </div>
                  ))}
                </>
              ) : multi ? (
                <>
                  <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                    {multi.beforeAll}
                  </ReactMarkdown>
                  {multi.sections.map((section, idx) => {
                    const { examples, explanationsMap } = sectionData[idx];
                    return (
                      <div key={idx} className="mb-6">
                        {examples.map(ex => (
                          <div key={ex.number}>
                            <CodeExampleBlock language={section.codeLang} example={ex} />
                            <ExplanationBlock markdown={explanationsMap[ex.number] || explanationsMap.general || ''} />
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                    {multi.afterAll}
                  </ReactMarkdown>
                </>
              ) : (
                <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                  {lesson.content}
                </ReactMarkdown>
              )}
            </div>
            {/* Quiz */}
            {!hasNestedLessons && !hasSubTopics && renderQuizInteractive(lesson)}
            
            {/* Lesson Completion */}
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-1">Lesson Progress</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {isCompleted ? "✅ You have completed this lesson" : "Mark this lesson as complete when you're done"}
                  </p>
                </div>
                <button
                  onClick={markLessonComplete}
                  disabled={completingLesson || isCompleted}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    isCompleted 
                      ? "bg-green-500 text-white cursor-default" 
                      : "bg-primary hover:bg-primary/90 text-white disabled:opacity-50"
                  }`}
                >
                  {completingLesson 
                    ? "Saving..." 
                    : isCompleted 
                      ? "✓ Completed" 
                      : "Mark Complete"
                  }
                </button>
              </div>
            </div>
          </div>
          <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
            Duration: {lesson.duration || "N/A"} min | {lesson.isPublished ? "Published" : "Draft"}
          </div>
        </div>
      </main>
    </div>
  );
  
};

export default LessonDetailPage;
